import { useCallback, useState } from 'react';
import { api } from '../../lib/api/client.js';
import { useAudio, type AudioState } from '../../lib/audio.js';
import { AudioControl, type AudioControlState } from '../../ui/components/AudioControl/AudioControl.tsx';
import { Dropzone } from '../../ui/primitives/Dropzone.tsx';
import { Field } from '../../ui/primitives/Field.tsx';
import { Text } from '../../ui/primitives/Text.tsx';
import { violationsFor, type FieldViolation } from './validation.js';
import styles from './AudioUpload.module.css';

interface AudioUploadProps {
  audioUrl: string;
  violations: readonly FieldViolation[];
  onChange: (audioUrl: string) => void;
}

const AUDIO_STATE_MAP: Record<AudioState, AudioControlState> = {
  idle: 'idle',
  loading: 'loading',
  playing: 'playing',
  played: 'replay',
  error: 'error',
};

/**
 * The clip a `note_identification` Exercise plays, admin-uploaded.
 *
 * Upload and authoring are two requests, not one: the file goes to
 * `POST /api/admin/media/audio` (`../../lib/api/client.js`
 * `uploadAdminAudio`) the moment it's chosen, the returned URL is what this
 * field actually holds, and the preview below plays that exact URL — the
 * same `useAudio` hook and `AudioControl` a learner's screen uses, so what
 * the admin hears here is what practice will play.
 */
export function AudioUpload({ audioUrl, violations, onChange }: AudioUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const audio = useAudio(audioUrl || null);

  const audioViolations = violationsFor(violations, 'config.audioUrl');

  const handleFile = useCallback(
    async (file: File) => {
      setUploading(true);
      setUploadError(null);
      try {
        const { url } = await api.uploadAdminAudio(file);
        onChange(url);
      } catch {
        setUploadError('Não foi possível enviar o arquivo. Tente novamente.');
      } finally {
        setUploading(false);
      }
    },
    [onChange],
  );

  return (
    <div className={styles.field} data-testid="audio-upload">
      <Field
        size="compact"
        label="Arquivo de áudio"
        htmlFor="audio-file"
        error={uploadError ?? audioViolations[0]?.message}
        hint={audioUrl ? undefined : 'MP3, WAV, OGG, AAC ou M4A.'}
      >
        <Dropzone
          id="audio-file"
          accept="audio/*"
          size="compact"
          disabled={uploading}
          invalid={Boolean(uploadError ?? audioViolations[0])}
          onFiles={(files) => {
            const file = files[0];
            if (file) void handleFile(file);
          }}
        />
      </Field>

      {uploading ? (
        <Text as="p" variant="body-sm" tone="muted">
          Enviando…
        </Text>
      ) : null}

      {audioUrl && !uploading ? (
        <div className={styles.preview} data-testid="audio-preview">
          <AudioControl
            state={AUDIO_STATE_MAP[audio.state]}
            label={audio.heard ? 'Ouvir novamente' : 'Ouvir'}
            errorMessage="Não foi possível tocar o arquivo enviado."
            size="compact"
            onActivate={audio.state === 'error' ? audio.retry : audio.play}
          />
        </div>
      ) : null}
    </div>
  );
}
